import { WebsiteModule } from '@/interfaces/module.interface';

const MODULE_STORAGE_KEY = 'builder-module-cache';
const MODULE_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

interface CachedModule {
  data: WebsiteModule;
  timestamp: number;
}

interface ModuleCache {
  [moduleId: string]: CachedModule;
}

// Get module cache from localStorage
function getModuleCache(): ModuleCache {
  try {
    const cached = localStorage.getItem(MODULE_STORAGE_KEY);
    if (!cached) return {};
    return JSON.parse(cached) as ModuleCache;
  } catch {
    return {};
  }
}

// Save module cache to localStorage
function saveModuleCache(cache: ModuleCache): void {
  try {
    localStorage.setItem(MODULE_STORAGE_KEY, JSON.stringify(cache));
  } catch (error) {
    console.warn('Failed to save module cache:', error);
  }
}

// Save module by id
export function saveModuleToStorage(module: WebsiteModule): void {
  const cache = getModuleCache();
  cache[module.id] = {
    data: module,
    timestamp: Date.now(),
  };
  saveModuleCache(cache);
}

// Get module by id
export function getModuleFromStorage(moduleId: string): WebsiteModule | null {
  const cache = getModuleCache();
  const entry = cache[moduleId];
  if (!entry) return null;

  const stale = Date.now() - entry.timestamp > MODULE_CACHE_TTL;
  if (stale) {
    // Remove stale entry
    delete cache[moduleId];
    saveModuleCache(cache);
    return null;
  }

  return entry.data;
}

// Get module by projectId
export function getModuleByProjectId(projectId: string): WebsiteModule | null {
  const cache = getModuleCache();
  const now = Date.now();

  for (const [moduleId, entry] of Object.entries(cache)) {
    if (entry.data.projectId === projectId) {
      const stale = now - entry.timestamp > MODULE_CACHE_TTL;
      if (stale) {
        // Remove stale entry
        delete cache[moduleId];
        saveModuleCache(cache);
        return null;
      }
      return entry.data;
    }
  }

  return null;
}

// Remove module by id
export function removeModuleFromStorage(moduleId: string): void {
  const cache = getModuleCache();
  delete cache[moduleId];
  saveModuleCache(cache);
}

// Clear all module cache
export function clearModuleCache(): void {
  try {
    localStorage.removeItem(MODULE_STORAGE_KEY);
  } catch (error) {
    console.warn('Failed to clear module cache:', error);
  }
}

// Clean up stale entries
export function cleanupStaleModules(): void {
  const cache = getModuleCache();
  const now = Date.now();
  let hasChanges = false;

  for (const [moduleId, entry] of Object.entries(cache)) {
    if (now - entry.timestamp > MODULE_CACHE_TTL) {
      delete cache[moduleId];
      hasChanges = true;
    }
  }

  if (hasChanges) {
    saveModuleCache(cache);
  }
}

